import { Color, Spacing } from '@persona-id/design-tokens';
import { ChevronLeftIcon, ChevronRightIcon } from '@persona-id/design-tokens/icons';
import { ComponentProps, useMemo, useRef } from 'react';
import Slider from 'react-slick';
import { SPACINGS } from 'shared/styles/variables';
import styled, { css, ThemeProvider } from 'styled-components';

import {
  generatePages,
  isFullscreenPage,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/pages';
import ThemedFonts from 'dashboard/components/ThemedFonts';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { ThemeAttributes } from 'dashboard/models/theme';
import { Z_INDICES } from 'dashboard/styles/variables';
import Button from 'design-system/components/Button';
import { Elevation } from 'design-system/variables/effect';
import { BASE } from 'design-system/variables/spacing';
import { colorCss } from 'lib/inquiryStyles';
import { ApiModel } from 'lib/interfaces/api';
import { isCombinedStep, type StepPreview } from 'lib/interfaces/StepsConfig';
import { FRAME_HEIGHT } from 'verify/components/Application/Frame';
import { serializeTheme } from 'verify/lib/theme';

import 'slick-carousel/slick/slick.css';

const slickArrowCss = css`
  cursor: pointer;
  position: absolute;
  top: 50%;
  z-index: ${Z_INDICES.modalElevated};
`;

/**
 * We vendor this in instead of importing slick-theme.css because
 * 1. The way slick-theme.css imports a SVG in the font declaration breaks
 *    OptimizeCssAssetsWebpackPlugin.
 * 2. We don't need half of the stuff in there (only dots code)
 * 3. This library is barely updated and we're not too worried about desyncing
 */
const vendoredSlickCssTheme = css`
  .slick-prev {
    ${slickArrowCss}
    left: -48px;
  }

  .slick-next {
    ${slickArrowCss}
    right: -48px;
  }

  .slick-prev.slick-disabled,
  .slick-next.slick-disabled {
    cursor: none;
    opacity: 0;
  }

  .slick-dots {
    position: absolute;
    bottom: -18px;

    width: 100%;
    text-align: center;
  }
  .slick-dots li {
    position: relative;
    display: inline-block;

    width: 20px;
    height: 20px;
    margin: 0 5px;

    cursor: pointer;
  }
  .slick-dots li button {
    font-size: 0;
    line-height: 0;
  }
  .slick-dots li button:hover,
  .slick-dots li button:focus {
    outline: none;
  }
  .slick-dots li button:hover:before,
  .slick-dots li button:focus:before {
    opacity: 1;
  }
  .slick-dots li button:before {
    font-size: 28px;
    line-height: 24px;

    position: absolute;
    top: 0;
    left: 0;

    width: 20px;
    height: 20px;

    content: '•';
    text-align: center;

    opacity: 0.25;
    color: black;

    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  .slick-dots li.slick-active button:before {
    opacity: 0.75;
    color: black;
  }
`;

const PreviewContainer = styled.div<{
  scaleRatio: number;
}>`
  ${vendoredSlickCssTheme};
  // scale dot size with scale ratio
  .slick-dots li button:before {
    font-size: ${({ scaleRatio }) => BASE * 6 * scaleRatio}px;
  }

  position: relative;
  flex: auto;
  height: 100%;
  background-color: ${Color.CoreSlate50};

  // NOTE: the following CSS solves the 'clicked dots remaining active' issue
  // https://github.com/akiran/react-slick/issues/1432
  .slick-dots li button:focus:before {
    opacity: 0.25;
  }
  .slick-dots li.slick-active button:focus:before,
  .slick-dots li button:hover:before {
    opacity: 1;
  }
`;

// Scaling explanation: https://css-tricks.com/scaled-proportional-blocks-with-css-and-javascript/
const ScalingContainer = styled.div<{
  scaleRatio: number;
  withMultiple: boolean;
}>`
  position: relative;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%) scale(${({ scaleRatio }) => scaleRatio});
  transform-origin: center center;
  width: calc(100% / ${({ scaleRatio }) => scaleRatio});

  ${({ withMultiple }) =>
    !withMultiple &&
    css`
      // centers preview when single screen
      .slick-track {
        display: grid;
        justify-content: center;
      }
    `};

  .slick-slide {
    transition: 0.3s;

    :not(.slick-active) {
      opacity: 0.5;
    }
  }
`;

const Slide = styled.div<{ isFullscreenPage: boolean }>`
  position: relative;
  height: ${FRAME_HEIGHT}px;
  width: 400px;
  pointer-events: none;
  user-select: none;
  margin: ${Spacing[6]};

  ${({ isFullscreenPage }) =>
    !isFullscreenPage &&
    css`
      // prettier-ignore
      padding: ${SPACINGS.navHeight} ${SPACINGS.widgetPadding} ${SPACINGS.widgetPadding} ${SPACINGS.widgetPadding};
    `};

  box-shadow: ${Elevation.High};

  // TODO dont rely on an extra themeing from dashboard, only use what the verify flow uses
  background-color: ${({ theme }) => theme.colorModalBackground};
  border-radius: ${({ theme }) => theme.borderRadiusModal};
  transition: 0.3s;
  overflow-y: hidden;
`;

interface InquiryTemplatePreviewProps {
  clientData: StepPreview[];
  theme?: ThemeAttributes;
  sliderSettings?: Partial<ComponentProps<typeof Slider>>;
  scaleRatio?: number;
}

const InquiryTemplatePreview = ({
  clientData,
  theme,
  sliderSettings,
  scaleRatio = 0.6,
}: InquiryTemplatePreviewProps) => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const slickRef = useRef<Slider>(undefined);

  const serializedTheme = serializeTheme({
    id: 'mockTheme',
    type: ApiModel.Theme,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    attributes: theme,
  });

  const pages = useMemo<React.ReactElement[]>(
    () => Object.values(generatePages(clientData)).flatMap((pages) => pages),
    [clientData],
  );
  const parentCombinedStep = useMemo(
    () => clientData.find((step) => isCombinedStep(step)),
    [clientData],
  );

  const previewContent = useMemo(() => {
    if (clientData.length > 0) {
      const settings: ComponentProps<typeof Slider> = {
        accessibility: true,
        arrows: true,
        dots: true,
        centerMode: true,
        focusOnSelect: true,
        infinite: false,
        swipeToSlide: true,
        // causes slides to take on their intrinsic width
        variableWidth: true,
        prevArrow: (
          <Button type='button' width='icon' variant='inverse'>
            <ChevronLeftIcon size={24} />
          </Button>
        ),
        nextArrow: (
          <Button type='button' width='icon' variant='inverse'>
            <ChevronRightIcon size={24} />
          </Button>
        ),
        ...sliderSettings,
      };
      return (
        <Slider {...settings} key={clientData[0].name} ref={slickRef}>
          {pages.map((page, key) => {
            // page key is generally derived from the step name, so by checking to see if it is a substring
            // of the step name, we can find the exact step. There are a few execeptions like device-handoff
            // where we will attempt to use its closest parent theme
            let themedStep;
            if (page.key?.toString().includes('device-handoff')) {
              themedStep = parentCombinedStep;
            } else {
              themedStep = clientData.find((step) => page.key?.toString().includes(step.name));
            }

            const backgroundColor =
              colorCss(themedStep?.styles?.['background-color'].base.base) ??
              serializedTheme.colorModalBackground;
            return (
              <ThemedFonts key={key}>
                <Slide isFullscreenPage={isFullscreenPage(page)} style={{ backgroundColor }}>
                  {page}
                </Slide>
              </ThemedFonts>
            );
          })}
        </Slider>
      );
    } else {
      return <LoadingIndicator />;
    }
  }, [clientData, pages, serializedTheme, sliderSettings]);

  return (
    <PreviewContainer scaleRatio={scaleRatio} data-test='step-preview-section__container'>
      <ScalingContainer scaleRatio={scaleRatio} withMultiple={pages?.length > 1}>
        <ThemeProvider theme={serializedTheme}>{previewContent}</ThemeProvider>
      </ScalingContainer>
    </PreviewContainer>
  );
};

export default InquiryTemplatePreview;
